import React, { useContext, useEffect, useState } from 'react';
import { HomeContext } from 'screens/Home/Home';
import { GlobalContext } from 'utilities/GlobalContext';
import AnimationLayer from '../AnimationLayer/AnimationLayer';
import './Grid.module.scss';

type Props = {
    children: React.ReactNode;
};

export default function Grid({ children }: Props) {
    const [dragging, setDragging] = useState(false);
    const {
        currentTile,
        gridRef,
        panZoomLibrary,
        setHandleZoom,
        theme,
    } = useContext(HomeContext);
    const { selected } = useContext(GlobalContext);

    let previousTouch: React.Touch | null = null;
    let initialPinchDistance: number | null = null;

    function handlePan(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        if (!dragging) return;
        event.preventDefault();
        panZoomLibrary.panBy({
            originX: event.movementX,
            originY: event.movementY,
        });
    }

    function handleTouchPan(event: React.TouchEvent<HTMLDivElement>) {
        if (!dragging) return;
        event.preventDefault();
        const touch = event.touches[0];

        if (previousTouch) {
            // be aware that these only store the movement of the first touch in the touches array
            const movementX = (touch.pageX - previousTouch.pageX) * 1.2;
            const movementY = (touch.pageY - previousTouch.pageY) * 1.2;
            panZoomLibrary.panBy({ originX: movementX, originY: movementY });
        }

        previousTouch = touch;
    }

    function handleZoom({
        event,
        direction,
    }: {
        event?: WheelEvent;
        direction?: number;
    }) {
        if (event) {
            // if (!event.ctrlKey && !direction) {
            //     return;
            // }
            event.preventDefault();
        }

        const deltaScale =
            direction || (Math.sign(event?.deltaY!) > 0 ? -1 : 1);
        const body = document.querySelector('body');

        const clientWidth = body?.clientWidth!;
        const clientHeight = body?.clientHeight!;

        const coordinates = {
            x: direction ? clientWidth / 2 : event?.pageX,
            y: direction ? clientHeight / 2 : event?.pageY,
        };

        panZoomLibrary.zoom({ ...coordinates, deltaScale });
    }

    function handleMouseDown(
        e:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.TouchEvent<HTMLDivElement>
    ) {
        e.preventDefault();
        setDragging(true);
    }

    function handleMouseUp() {
        if (dragging) setDragging(false);
        if (previousTouch) previousTouch = null;
        if (initialPinchDistance) initialPinchDistance = null;
    }

    function handleScrollZoom(event: WheelEvent) {
        handleZoom({ event });
    }

    function preventDefaultTouch(event: TouchEvent) {
        event.preventDefault();
    }

    function handlePinchZoom(event: TouchEvent) {
        if (event.touches.length === 2) {
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];

            const dx = touch1.pageX - touch2.pageX;
            const dy = touch1.pageY - touch2.pageY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (initialPinchDistance !== null) {
                // Adjust the zoom factor here
                const zoomFactor = 0.2; // Lower value for less aggressive zoom
                const direction = distance > initialPinchDistance ? 1 : -1;
                handleZoom({ direction: direction * zoomFactor });
            }

            initialPinchDistance = distance;
        }
    }

    useEffect(() => {
        if (panZoomLibrary && selected === 'initial') {
            window.addEventListener('wheel', handleScrollZoom, {
                passive: false,
            });
            window.addEventListener('touchmove', preventDefaultTouch, {
                passive: false,
            });
            window.addEventListener('touchmove', handlePinchZoom, {
                passive: false,
            });
            window.addEventListener('touchend', handleMouseUp, {
                passive: false,
            });

            if (selected !== 'initial') {
                window.removeEventListener('touchmove', handlePinchZoom);
            }

            return () => {
                window.removeEventListener('wheel', handleScrollZoom);
                window.removeEventListener('touchmove', handlePinchZoom);
                window.removeEventListener('touchmove', preventDefaultTouch);
                window.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [panZoomLibrary, selected]);

    useEffect(() => {
        if (panZoomLibrary) {
            panZoomLibrary.panTo({
                originX: currentTile.originX,
                originY: currentTile.originY,
            });
            const screen = document.querySelector('body')?.clientWidth!;

            const query = {
                mobileSmall: screen <= 400,
                tablet: screen > 768 && screen <= 1000,
                desktop: screen > 1000 && screen <= 1600,
                desktopLarge: screen > 1600,
            };
            const zoomNTimes = (n: number) => Array.from(Array(n).keys());

            if (query.mobileSmall)
                zoomNTimes(1).forEach(() => handleZoom({ direction: -1 }));
            if (query.tablet)
                zoomNTimes(1).forEach(() => handleZoom({ direction: 1 }));
            if (query.desktop)
                zoomNTimes(2).forEach(() => handleZoom({ direction: 1 }));
            if (query.desktopLarge)
                zoomNTimes(3).forEach(() => handleZoom({ direction: 1 }));
        }
    }, [currentTile, panZoomLibrary]);

    useEffect(() => {
        if (panZoomLibrary) setHandleZoom(handleZoom);
    }, [panZoomLibrary]);

    return (
        <div
            styleName="home-grid"
            ref={gridRef}
            onMouseMove={handlePan}
            onTouchMove={handleTouchPan}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
            style={{ cursor: dragging ? 'grab' : '' }}
        >
            <img
                styleName="background"
                src={
                    theme === 'winter'
                        ? '/images/home-backdrops/winter.svg'
                        : theme === 'spring'
                        ? '/images/home-backdrops/lente.svg'
                        : theme === 'fall'
                        ? '/images/home-backdrops/herfst.svg'
                        : '/images/home-backdrops/zomer.svg'
                }
                alt="background"
            />
            <AnimationLayer />
            {children}
        </div>
    );
}
